import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { registrationsAPI } from '../services/api';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { getErrorMessage } from '../utils/errorMessage';
import { clubLogoSrc, handleClubLogoError } from '../utils/clubLogo';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, validatePassword } from '../utils/passwordPolicy';

/**
 * Önkiszolgáló regisztráció. Bárki kitöltheti, de fiók NEM jön létre belőle:
 * a jelentkezés admin jóváhagyásra vár, és a szerepkört is az admin adja meg.
 * Ezért nincs is szerepkör-választó az űrlapon - csak egy jelzés, hogy
 * szülőként jönne-e.
 */
const Register: React.FC = () => {
  usePageTitle('Regisztráció');
  const { settings: clubSettings } = useClubSettings();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [wantsParentAccount, setWantsParentAccount] = useState(false);
  const [note, setNote] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('A két jelszó nem egyezik.');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    try {
      await registrationsAPI.submit({
        name: name.trim(),
        email: email.trim(),
        password,
        wantsParentAccount,
        note: note.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err, 'A jelentkezést nem sikerült elküldeni.'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-4">
            <img
              src={clubLogoSrc(clubSettings?.crestUrl)}
              alt=""
              className="h-20 w-20 object-contain"
              onError={handleClubLogoError}
            />
          </div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {submitted ? 'Jelentkezés elküldve' : 'Regisztráció'}
          </h2>
          {!submitted && (
            <p className="mt-2 text-center text-sm text-gray-600">
              {clubSettings?.clubName || 'A klub'} rendszerébe
            </p>
          )}
        </div>

        {submitted ? (
          <div className="space-y-6">
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex gap-3">
                <CheckCircleIcon className="h-6 w-6 shrink-0 text-green-600" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">Köszönjük, megkaptuk a jelentkezésedet.</p>
                  <p className="mt-1">
                    Az adminisztrátor hagyja jóvá, addig még nem tudsz belépni. A döntésről
                    emailben értesítünk. Jóváhagyás után azzal az email címmel és jelszóval
                    tudsz bejelentkezni, amit most megadtál.
                  </p>
                </div>
              </div>
            </div>

            <Link
              to="/login"
              className="block w-full rounded-md bg-primary-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-primary-700"
            >
              Vissza a bejelentkezéshez
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="reg-name" className="mb-1 block text-sm font-medium text-gray-700">
                  Felhasználó neve *
                </label>
                <input
                  id="reg-name"
                  type="text"
                  autoComplete="name"
                  required
                  minLength={2}
                  className={inputClass}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="reg-email" className="mb-1 block text-sm font-medium text-gray-700">
                  Email cím *
                </label>
                <input
                  id="reg-email"
                  type="email"
                  autoComplete="email"
                  required
                  className={inputClass}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="reg-password" className="mb-1 block text-sm font-medium text-gray-700">
                  Jelszó *
                </label>
                <div className="relative">
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className={`${inputClass} pr-10`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">{PASSWORD_HINT}</p>
              </div>

              <div>
                <label htmlFor="reg-password2" className="mb-1 block text-sm font-medium text-gray-700">
                  Jelszó megerősítése *
                </label>
                <input
                  id="reg-password2"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className={inputClass}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                />
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={wantsParentAccount}
                  onChange={(e) => setWantsParentAccount(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Szülőként regisztrálok
              </label>

              <div>
                <label htmlFor="reg-note" className="mb-1 block text-sm font-medium text-gray-700">
                  Megjegyzés az adminisztrátornak
                </label>
                <textarea
                  id="reg-note"
                  rows={3}
                  maxLength={500}
                  placeholder="Pl. melyik játékos szülője vagy - ez segít a jóváhagyásban."
                  className={inputClass}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Küldés...' : 'Jelentkezés elküldése'}
            </button>

            <p className="text-center text-sm text-gray-600">
              Van már fiókod?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Bejelentkezés
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Register;
